import { PropsWithChildren, useCallback, useRef, useState } from "react";
import { Search } from "lucide-react";
import { isMacOs } from "react-device-detect";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_atoms/table";
import { IconXCircleFilled } from "@/components/_icons/IconXCircleFilled";
import { FirstItemCelebration } from "@/components/_molecules/creation-celebration/first-item-celebration";
import { TableEmptyState, TableEmptyStateProps } from "@/components/_molecules/table-empty-state/table-empty-state";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

interface DataTableProps<TData, TValue> extends PropsWithChildren {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  defaultSort?: SortingState;
  emptyState?: TableEmptyStateProps;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  defaultSort,
  children,
  emptyState,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation("common");
  const showEmptyState = data.length === 0 && !isLoading && emptyState !== undefined;
  const shortcut = isMacOs ? "\u2318" : "Ctrl";
  const placeholderText = t("fileDatatable.filterPlaceholder", { shortcut });
  const inputRef = useRef<HTMLInputElement>(null);

  const hasNameColumn = columns.some((col) => col.id === "name");

  const [sorting, setSorting] = useState<SortingState>(defaultSort ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const focusInput = useCallback(() => {
    inputRef?.current?.focus();
  }, [inputRef]);

  useHotkeys("mod+f", focusInput, { preventDefault: true });

  const nameFilterValue = (table.getColumn("name")?.getFilterValue() as string) ?? "";

  const clearInput = () => {
    table.getColumn("name")?.setFilterValue("");
  };

  return (
    <div>
      <div className="chrome-row flex shrink-0 items-center justify-between gap-2 border-b border-solid">
        {hasNameColumn && (
          <div className="group relative w-full">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={inputRef}
              placeholder={placeholderText}
              aria-label={placeholderText}
              value={nameFilterValue}
              onChange={(event) => {
                table.getColumn("name")?.setFilterValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  clearInput();
                }
              }}
              className="h-7 w-full rounded-md border pl-8 shadow-none placeholder:text-xs"
            />
            {nameFilterValue !== "" && (
              <Button
                variant="ghost"
                size="icon"
                className="invisible absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full p-0 hover:bg-transparent focus-visible:visible group-focus-within:visible group-hover:visible"
                onClick={clearInput}
                aria-label={t("search.clear")}
              >
                <IconXCircleFilled className="h-3 w-3 text-foreground/60" aria-hidden="true" />
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-row gap-1">{children}</div>
      </div>
      <div className="overflow-y-scroll" style={{ maxHeight: "calc(100vh - 170px)" }}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="first:pl-0">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="group border-none text-sm"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="first:pl-0">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-auto py-8 text-center">
                  {showEmptyState ? <TableEmptyState {...emptyState} /> : t("fileDatatable.noDocuments")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex-1 py-2 text-xs text-muted-foreground">
        {t("fileDatatable.entryCount", { count: table.getFilteredRowModel().rows.length })}
      </div>
      <FirstItemCelebration itemCount={data.length} enabled={!isLoading} />
    </div>
  );
}
